import { TranslatePipe } from "@ngx-translate/core";

import { ViewportScroller } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { Router } from "@angular/router";

import { ServiceType } from "../../../apis/networkdashboard/graphql/graphql";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { ServiceSpeedPipe } from "../../../helpers/service-speed-pipe/service-speed-pipe";
import { isUntaggedPort } from "../../../helpers/untaggedport";
import { phraseDetailPath } from "../../../pages/subscription-detail/subscription-detail-redirection";
import { AuthService } from "../../../services/authservice";
import { MessageService } from "../../../services/messageservice";
import { serviceHealth } from "../../dashboard/service-health-signal/service-health-signal";
import { ServiceTitleBlock } from "../service-title-block/service-title-block";

export interface ServiceSummaryService {
  subscriptionId: string;
  customerId: string;
  description: string;
  customerDescription: string;
  product: { type: string; tag: string };
  organisation?: string | null;
  tags?: string[] | null;
  customer?: { fullname?: string | null } | null;
  locations?: Array<{
    address?: {
      city?: string | null;
      street?: string | null;
      number?: string | null;
      addition?: string | null;
    } | null;
  }> | null;
  firewallEnabled?: boolean | null;
  port?: { serviceTag?: string | null } | null;
  ipGateway?: { serviceSpeed: number } | null;
}

@Component({
  selector: "service-summary",
  templateUrl: "service-summary.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [GuidPipe, ServiceSpeedPipe, ServiceTitleBlock, TranslatePipe],
})
export class ServiceSummary {
  private auth = inject(AuthService);
  private msgsrv = inject(MessageService);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);

  protected service = input.required<ServiceSummaryService>();
  protected showTraffic = input(false);

  protected subscriptionInstanceId = input<string>();
  protected forceTrafficBottom = input(false);
  protected showTrafficButton = input<boolean>();
  protected vlanranges = input<string[]>([]);

  protected isAccessible = computed(() => this.auth.viewingCustomer().customerId === this.service().customerId);
  protected health = serviceHealth(this.service);

  protected subscriptionId = computed(() => this.service().subscriptionId);
  private product = computed(() => this.service().product);
  protected tags = computed(() => this.service().tags ?? []);
  private isMsc = computed(() => this.product().tag.startsWith("MSC"));
  protected isPort = computed(() => this.product().type === "Port");
  protected showFirewallEnabled = computed(() => !!this.service().firewallEnabled);
  protected showVlanranges = computed(() => this.vlanranges()?.length > 0 && !this.isMsc());
  protected vlanrangeRows = computed(() =>
    this.vlanranges().map((vlanrange) => ({ vlanrange, untagged: isUntaggedPort(vlanrange) })),
  );

  protected titleBlockVm = computed(() => ({
    subscriptionId: this.service().subscriptionId,
    product: this.service().product,
    ipGateway: !!this.service().ipGateway,
  }));

  protected organisationName = computed(() => this.service().organisation);

  protected title = computed(() => this.service().customerDescription ?? this.service().description);

  protected locations = computed(() =>
    (this.service().locations ?? []).map(
      (l) => `${l.address?.city} ${l.address?.street} ${l.address?.number}${l.address?.addition ?? ""}`,
    ),
  );

  protected navigateToService(service: ServiceType) {
    this.router.navigate(phraseDetailPath(service.product.type, service.product.tag, service.subscriptionId));
  }

  protected navigateToChart() {
    this.viewportScroller.scrollToAnchor("chart");
    const filterProps = {
      connection: this.service(),
      vlanranges: this.vlanranges(),
    };

    this.msgsrv.setChartFilter(filterProps);
  }
}
